import { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { Container, Typography, Tabs, Tab, Box } from "@mui/material";

import CardInfoGeneral from "./cardInfoGeneral";
import CardPDFs from "./cardPDFs";
import CardDeudas from "./cardDeudas";
import CardBienes from "./cardBienes";
import CardAdicionales from "./cardAdicionales";
import CarFinanzas from "./cardFinanzas";
import CardAnexos from "./cardAnexos";
import ProcesosApi from "api/procesosApi";

const ContainerResumen = ({ proceso }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [deudas, setDeudas] = useState([]);
  const [bienes, setBienes] = useState([]);
  const [adicionales, setAdicionales] = useState([]);
  const [finanzas, setFinanzas] = useState([]);
  const [propuestaPago, setPropuestaPago] = useState([]);

  useEffect(() => {
    async function cargarBienes() {
      if (!proceso?.id) return;
      try {
        const respBienes = await ProcesosApi.obtenerBienesProceso(proceso.id);
        const data = respBienes.data;
        setBienes(data.bienes);
      } catch (error) {
        console.error("Error al cargar bienes:", error);
        setBienes([]);
      }
    }
    cargarBienes();
  }, [proceso]);

  useEffect(() => {
    async function cargarDeudas() {
      if (!proceso?.id) return;
      try {
        const respDeudas = await ProcesosApi.obtenerDeudasProceso(proceso.id);
        const data = respDeudas.data;
        setDeudas(data.deudas);
      } catch (error) {
        console.error("Error al cargar deudas:", error);
        setDeudas([]);
      }
    }
    cargarDeudas();
  }, [proceso]);

  useEffect(() => {
    async function cargarAdicionales() {
      if (!proceso?.id) return;
      try {
        const [respPersonas, respProcesos, respSociedades] = await Promise.all([
          ProcesosApi.obtenerPersonasCargo(proceso.id),
          ProcesosApi.obtenerProcesosJudiciales(proceso.id),
          ProcesosApi.obtenerSociedades(proceso.id),
        ]);
        setAdicionales({
          personasCargo: respPersonas.data,
          procesosJudiciales: respProcesos.data,
          sociedades: respSociedades.data,
        });
      } catch (error) {
        console.error("Error al cargar adicionales:", error);
        setAdicionales({
          personasCargo: [],
          procesosJudiciales: [],
          sociedades: [],
        });
      }
    }
    cargarAdicionales();
  }, [proceso]);

  useEffect(() => {
    async function cargarFinanzas() {
      if (!proceso?.id) return;
      try {
        const [respFinanzas, respPropuestaPago] = await Promise.all([
          ProcesosApi.obtenerFinanzas(proceso.id),
          ProcesosApi.obtenerPropuestas(proceso.id),
        ]);
        const propuestaActiva = respPropuestaPago.data[0];
        if (propuestaActiva) {
          setPropuestaPago(propuestaActiva);
        }
        setFinanzas({
          finanzas: respFinanzas.data,
          propuestaPago: respPropuestaPago.data,
        });
      } catch (error) {
        console.error("Error al cargar finanzas:", error);
        setFinanzas({
          finanzas: [],
          propuestaPago: [],
        });
        setPropuestaPago([]);
      }
    }
    cargarFinanzas();
  }, [proceso]);

  const handleChange = (event, newIndex) => {
    setTabIndex(newIndex);
  };

  return (
    <Container>
      {proceso ? (
        <>
          <Tabs
            value={tabIndex}
            onChange={handleChange}
            variant="scrollable"
            textColor="inherit" // Asegura que el texto respete los estilos personalizados
            TabIndicatorProps={{
              style: { backgroundColor: "#1976d2" }, // Color del indicador
            }}
          >
            <Tab
              label={proceso.identificador}
              sx={{
                color: tabIndex === 0 ? "white !important" : "inherit",
                backgroundColor: tabIndex === 0 ? "#1976d2" : "#e3f2fd",
                fontWeight: tabIndex === 0 ? "bold" : "normal",
                marginRight: "8px", // Espacio entre pestañas
                transition: "0.3s",
              }}
            />
            <Tab
              label="PDF's Gestión"
              sx={{
                color: tabIndex === 1 ? "white !important" : "inherit",
                backgroundColor: tabIndex === 1 ? "#1976d2" : "#e3f2fd",
                fontWeight: tabIndex === 1 ? "bold" : "normal",
                marginRight: "8px", // Espacio entre pestañas
                transition: "0.3s",
              }}
            />
            <Tab
              label="Acreencias"
              sx={{
                color: tabIndex === 2 ? "white !important" : "inherit",
                backgroundColor: tabIndex === 2 ? "#1976d2" : "#e3f2fd",
                fontWeight: tabIndex === 2 ? "bold" : "normal",
                marginRight: "8px", // Espacio entre pestañas
                transition: "0.3s",
              }}
            />
            <Tab
              label="Bienes"
              sx={{
                color: tabIndex === 3 ? "white !important" : "inherit",
                backgroundColor: tabIndex === 3 ? "#1976d2" : "#e3f2fd",
                fontWeight: tabIndex === 3 ? "bold" : "normal",
                marginRight: "8px", // Espacio entre pestañas
                transition: "0.3s",
              }}
            />
            <Tab
              label="Adicionales"
              sx={{
                color: tabIndex === 4 ? "white !important" : "inherit",
                backgroundColor: tabIndex === 4 ? "#1976d2" : "#e3f2fd",
                fontWeight: tabIndex === 4 ? "bold" : "normal",
                marginRight: "8px", // Espacio entre pestañas
                transition: "0.3s",
              }}
            />
            <Tab
              label="Propuesta Pago"
              sx={{
                color: tabIndex === 5 ? "white !important" : "inherit",
                backgroundColor: tabIndex === 5 ? "#1976d2" : "#e3f2fd",
                fontWeight: tabIndex === 5 ? "bold" : "normal",
                marginRight: "8px", // Espacio entre pestañas
                transition: "0.3s",
              }}
            />
            <Tab
              label="Docs. Anexos"
              sx={{
                color: tabIndex === 6 ? "white !important" : "inherit",
                backgroundColor: tabIndex === 6 ? "#1976d2" : "#e3f2fd",
                fontWeight: tabIndex === 6 ? "bold" : "normal",
                marginRight: "8px", // Espacio entre pestañas
                transition: "0.3s",
              }}
            />
          </Tabs>

          <Box sx={{ p: 1, border: "0px solid #ddd", borderRadius: "2px", mt: 2 }}>
            {tabIndex === 0 && <CardInfoGeneral proceso={proceso} />}
            {tabIndex === 1 && <CardPDFs proceso={proceso} />}
            {tabIndex === 2 && <CardDeudas proceso={proceso} deudas={deudas} />}
            {tabIndex === 3 && <CardBienes proceso={proceso} bienes={bienes} />}
            {tabIndex === 4 && <CardAdicionales proceso={proceso} adicionales={adicionales} />}
            {tabIndex === 5 && (
              <CarFinanzas
                proceso={proceso}
                finanzas={finanzas?.finanzas}
                propuestaPago={propuestaPago}
              />
            )}
            {tabIndex === 6 && <CardAnexos proceso={proceso} />}
          </Box>
        </>
      ) : (
        <Typography>Cargando...</Typography>
      )}
    </Container>
  );
};

ContainerResumen.propTypes = {
  proceso: PropTypes.shape({
    id: PropTypes.number.isRequired,
    identificador: PropTypes.string.isRequired,
    deudas: PropTypes.array,
    bienes: PropTypes.array,
    adicionales: PropTypes.array,
    finanzas: PropTypes.object,
    propuesta_pago: PropTypes.object,
    documentos: PropTypes.array,
  }),
};

export default ContainerResumen;
